import * as L from 'leaflet';

export interface MapViewCenter {
    lat: number;
    lng: number;
}

export interface MapViewState {
    center: MapViewCenter;
    zoom: number;
    bearing: number;
}

const CENTER_EPSILON = 1e-6;
const ZOOM_DECIMALS = 2;
const FULL_CIRCLE_DEGREES = 360;

export function roundMapZoom(value: number): number {
    const factor = Math.pow(10, ZOOM_DECIMALS);
    return Math.round(Number(value) * factor) / factor;
}

export function normalizeMapBearing(value: number): number {
    return ((Number(value) % FULL_CIRCLE_DEGREES) + FULL_CIRCLE_DEGREES) % FULL_CIRCLE_DEGREES;
}

export function toMapViewCenter(
    center: MapViewCenter | L.LatLng | { lat: number; lng: number } | [number, number] | null | undefined,
): MapViewCenter | null {
    if (center == null) {
        return null;
    }

    if (Array.isArray(center)) {
        const lat = Number(center[0]);
        const lng = Number(center[1]);
        return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    }

    const lat = Number((center as MapViewCenter).lat);
    const lng = Number((center as MapViewCenter).lng);
    return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

export function mapCentersEqual(
    a: MapViewCenter | L.LatLng | [number, number] | null | undefined,
    b: MapViewCenter | L.LatLng | [number, number] | null | undefined,
    epsilon = CENTER_EPSILON,
): boolean {
    const left = toMapViewCenter(a);
    const right = toMapViewCenter(b);
    if (!left || !right) {
        return false;
    }
    return Math.abs(left.lat - right.lat) < epsilon && Math.abs(left.lng - right.lng) < epsilon;
}

export function readMapView(map: L.Map): MapViewState {
    const center = map.getCenter();
    return {
        center: { lat: center.lat, lng: center.lng },
        zoom: map.getZoom(),
        bearing: typeof (map as any).getBearing === 'function' ? map.getBearing() : 0,
    };
}

export function isMapViewDirty(current: MapViewState, saved?: Partial<MapViewState> | null): boolean {
    if (!saved) {
        return true;
    }

    const zoomDirty = saved.zoom == null || roundMapZoom(current.zoom) !== roundMapZoom(saved.zoom);
    const bearingDirty = normalizeMapBearing(current.bearing)
        !== normalizeMapBearing(saved.bearing ?? 0);
    const centerDirty = saved.center == null || !mapCentersEqual(current.center, saved.center);

    return zoomDirty || bearingDirty || centerDirty;
}

/**
 * Applies center, zoom and bearing in one origin-resyncing setView.
 * Bearing first (leaflet-rotate), then setView so `_pixelOrigin` matches the restored camera.
 */
export function applySavedMapView(
    map: L.Map,
    view: Partial<MapViewState>,
    options?: { animate?: boolean },
): void {
    const current = readMapView(map);
    const center = toMapViewCenter(view.center) ?? current.center;
    const zoom = view.zoom ?? current.zoom;
    const bearing = view.bearing ?? current.bearing;

    if (typeof (map as any).setBearing === 'function') {
        map.setBearing(bearing);
    }

    map.setView([center.lat, center.lng], zoom, { animate: options?.animate ?? false });
}
